import React, {DragEvent, useRef, useState} from 'react';
import {UploadService} from "../../service/UploadService";

export type ImageUploadProps = {
  label: string;
  // Image actuellement enregistrée
  current?: string;
  // Plus grand côté de l'image après redimensionnement
  maxSize: number;
  deletable?: boolean;
  error?: string;
  // undefined : inchangée · '' : à supprimer · base64 : nouvelle image
  change: (value: undefined | string) => void;
}

export const ImageUpload = ({label, current, maxSize, deletable = true, error, change}: ImageUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [value, setValue] = useState(undefined as undefined | string);
  const [processing, setProcessing] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const update = (v: undefined | string): void => {
    setValue(v);
    change(v);
  }

  const handleFile = async (file?: File): Promise<void> => {
    if (!file || !file.type.startsWith('image/')) {
      return;
    }

    setProcessing(true);

    try {
      update(await UploadService.resizeImageToBase64(file, maxSize));
    } finally {
      setProcessing(false);
    }
  }

  const onDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  }

  const pick = (): void => {
    if (!processing) {
      inputRef.current?.click();
    }
  }

  const deleted: boolean = value === '';
  const preview: undefined | string = deleted ? undefined : (value ?? current);

  return (
    <div className="mb-3">
      <label className="form-label d-block">{label}</label>

      <div className={`__admin_dropzone__ ${dragOver ? '__admin_dropzone_over__' : ''} ${preview ? '__admin_dropzone_filled__' : ''}`}
           onClick={() => !preview && pick()}
           onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
           onDragLeave={() => setDragOver(false)}
           onDrop={onDrop}>
        <input ref={inputRef} type="file" accept="image/*" hidden
               onChange={(e) => {
                 handleFile(e.target.files?.[0]);
                 e.target.value = '';
               }}/>

        {processing && (
          <div className="spinner-border text-primary" role="status"/>
        )}

        {!processing && preview && (
          <img src={preview} alt={label} className="__admin_dropzone_preview__"/>
        )}

        {!processing && !preview && (
          <>
            <i className="fa-solid fa-cloud-arrow-up fs-2 mb-2"/>
            <div className="fw-bold">Glissez une image ici ou cliquez pour la choisir</div>
            <div className="small text-muted">L'image est redimensionnée automatiquement</div>
          </>
        )}
      </div>

      <div className="d-flex flex-wrap align-items-center gap-1 mt-2">
        {value !== undefined && (
          <span className="small fst-italic me-auto">
            {deleted ? "L'image sera supprimée à l'enregistrement" : "Nouvelle image, enregistrée avec le formulaire"}
          </span>
        )}
        {preview && (
          <button type="button" className="btn btn-xs btn-primary" onClick={pick} disabled={processing}>
            <i className="fa-solid fa-arrows-rotate me-1"/>Remplacer
          </button>
        )}
        {preview && deletable && current && value === undefined && (
          <button type="button" className="btn btn-xs btn-danger" onClick={() => update('')}>
            <i className="fa-solid fa-trash me-1"/>Supprimer
          </button>
        )}
        {value !== undefined && (
          <button type="button" className="btn btn-xs btn-info" onClick={() => update(undefined)}>
            Annuler
          </button>
        )}
      </div>

      {error && <p className="text-danger mt-1 mb-0">{error}</p>}
    </div>
  );
}
